import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Plus } from 'lucide-react'
import { toast } from 'react-hot-toast'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuth } from '@/contexts/auth-context'
import { useCreateOrganization, type CreateOrganizationError } from '@/hooks/api-hooks'

const formSchema = z.object({
  name: z
    .string()
    .min(2, { message: 'Organization name must be at least 2 characters long' })
    .max(255, { message: 'Organization name must be 255 characters or fewer' }),
  plan: z.string().min(1, { message: 'Please select a plan' }),
})

const mapPlanForUser = (plan: string): 'free' | 'professional' | 'enterprise' => {
  const normalized = plan.toLowerCase()
  if (normalized.includes('pro')) return 'professional'
  if (normalized.includes('enterprise')) return 'enterprise'
  return 'free'
}

const mapRoleForUser = (role: string): 'owner' | 'admin' | 'manager' | 'staff' => {
  const normalized = role.toLowerCase()
  if (normalized === 'owner') return 'owner'
  if (normalized === 'admin') return 'admin'
  if (normalized === 'manager') return 'manager'
  return 'staff'
}

type CreateOrganizationFormValues = z.infer<typeof formSchema>

export function CreateOrganizationPage() {
  const navigate = useNavigate()
  const { user, loading: authLoading, updateUser } = useAuth()
  const createOrganizationMutation = useCreateOrganization()

  const form = useForm<CreateOrganizationFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      plan: '',
    },
  })

  const organizations = Array.isArray(user?.organizations) ? user?.organizations ?? [] : []
  const hasFreePlan = organizations.some((org) => {
    const normalizedPlan = String(org.plan ?? '').toUpperCase()
    const normalizedRole = String(org.role ?? '').toUpperCase()
    return normalizedPlan === 'FREE' && normalizedRole === 'OWNER'
  })

  const isSubmitting = createOrganizationMutation.isPending

  const handleSubmit = async (values: CreateOrganizationFormValues) => {
    console.info('[CreateOrg] submit', values)

    if (values.plan === 'FREE' && hasFreePlan) {
      toast.error('You already own a Free plan organization. Please select a paid plan.')
      return
    }

    try {
      const response = await createOrganizationMutation.mutateAsync(values)
      console.info('[CreateOrg] response', response)

      if (!response?.success || !response?.data) {
        const message = response?.error ?? response?.message ?? 'Failed to create organization'
        throw new Error(message)
      }

      const createdOrg = response.data
      const mappedPlan = mapPlanForUser(createdOrg.plan)
      const mappedRole = mapRoleForUser(createdOrg.role)

      toast.success(response.message ?? 'Organization created successfully')
      sessionStorage.removeItem('pendingOrganization')

      if (user) {
        const enrichedOrganization = {
          id: createdOrg.id,
          name: createdOrg.name,
          avatar: undefined,
          plan: mappedPlan,
          role: mappedRole,
          industry: createdOrg.industry ?? undefined,
          companySize: createdOrg.companySize ?? undefined,
          website: createdOrg.website ?? undefined,
          description: createdOrg.description ?? undefined,
          timeZone: undefined,
          createdAt: createdOrg.createdAt,
          updatedAt: createdOrg.createdAt,
        }

        const filteredOrganizations = organizations.filter((org) => org.id !== createdOrg.id)
        updateUser({
          organizations: [...filteredOrganizations, enrichedOrganization],
          currentOrganizationId: createdOrg.id,
          organizationRole: mappedRole,
        })
      }

      navigate('/dashboard')
    } catch (error) {
      const typedError = error as CreateOrganizationError
      const requiresPayment = typedError?.code === 'PAYMENT_REQUIRED' || typedError?.code === 'UPGRADE_REQUIRED'

      if (requiresPayment && typedError?.paymentLink) {
        toast((typedError.message ?? 'Payment required to create this organization'), {
          id: 'org-payment-required',
        })

        const pendingData: Record<string, unknown> =
          typedError.organizationData && typeof typedError.organizationData === 'object'
            ? (typedError.organizationData as Record<string, unknown>)
            : { ...values }

        const rawPlan = String(pendingData.plan ?? values.plan ?? '').toUpperCase()
        const allowedPlans = new Set(['STARTER', 'PROFESSIONAL', 'ENTERPRISE'])
        const normalizedPlan = allowedPlans.has(rawPlan) ? rawPlan : 'STARTER'

        sessionStorage.setItem(
          'pendingOrganization',
          JSON.stringify({
            ...pendingData,
            action: typedError.action ?? 'CREATE_PAID_ORG',
            plan: normalizedPlan,
          }),
        )

        console.info('[CreateOrg] pending organization stored', {
          ...pendingData,
          plan: normalizedPlan,
        })

        try {
          sessionStorage.setItem(
            'pendingBilling',
            JSON.stringify({
              organizationId: null,
              planId: normalizedPlan,
              sessionId: null,
              source: 'payment_link',
              organization: {
                name: String(pendingData.name ?? values.name ?? ''),
                description:
                  typeof pendingData.description === 'string'
                    ? pendingData.description
                    : undefined,
                industry:
                  typeof pendingData.industry === 'string'
                    ? pendingData.industry
                    : undefined,
                companySize:
                  typeof pendingData.companySize === 'string'
                    ? pendingData.companySize
                    : undefined,
              },
              storedAt: new Date().toISOString(),
            }),
          )
          console.info('[CreateOrg] pending billing stored', {
            planId: normalizedPlan,
            organization: {
              name: pendingData.name ?? values.name,
              description: pendingData.description,
              industry: pendingData.industry,
              companySize: pendingData.companySize,
            },
          })
        } catch (storageError) {
          console.warn('Failed to persist pending billing info for new organization', storageError)
        }

        window.location.href = typedError.paymentLink
        return
      }

      const fallbackMessage = typedError?.message ?? 'Failed to create organization'
      toast.error(fallbackMessage)
    }
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Create Organization</h1>
          <p className="text-muted-foreground">Set up a new organization and become its owner.</p>
        </div>
      </div>

      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>Organization Details</CardTitle>
          <CardDescription>Provide the information for your new organization.</CardDescription>
        </CardHeader>

        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Organization Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter your company name" {...field} disabled={isSubmitting} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="plan"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Plan Selection</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value} disabled={isSubmitting}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a plan" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {!hasFreePlan && (
                          <SelectItem value="FREE">
                            <div className="flex flex-col">
                              <span className="font-medium">Free Plan</span>
                              <span className="text-xs text-muted-foreground">Basic features for small teams</span>
                            </div>
                          </SelectItem>
                        )}
                        <SelectItem value="STARTER">
                          <div className="flex flex-col">
                            <span className="font-medium">Starter Plan - $29/month</span>
                            <span className="text-xs text-muted-foreground">Perfect for growing teams</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="PROFESSIONAL">
                          <div className="flex flex-col">
                            <span className="font-medium">Professional Plan - $79/month</span>
                            <span className="text-xs text-muted-foreground">Advanced features for larger teams</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="ENTERPRISE">
                          <div className="flex flex-col">
                            <span className="font-medium">Enterprise Plan - $199/month</span>
                            <span className="text-xs text-muted-foreground">Full features for enterprise</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                    {hasFreePlan && (
                      <p className="text-xs text-muted-foreground mt-1">
                        You already have a Free plan organization. Paid plans require payment before creation.
                      </p>
                    )}
                  </FormItem>
                )}
              />

              <div className="flex gap-4">
                <Button type="button" variant="outline" onClick={() => navigate(-1)} className="flex-1" disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting} className="flex-1">
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Organization
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}
